/** Cool-white interaction light: a soft luminous edge and neutral surface sheen.
 * Widths are measured in CSS pixels; no colored fill or dark keyline. */
export const interactionShader = /* wgsl */ `
fn interactionEdgePixels(distance: f32) -> f32 {
  let footprint = max(length(vec2<f32>(dpdx(distance), dpdy(distance))), 0.00001);
  return distance / footprint / max(frame.eye.w, 0.001);
}

fn interactionSurface(input: VertexOut, color: vec3<f32>) -> vec3<f32> {
  let paying = object.surface.w < 0.0;
  let shape = abs(object.surface.w);
  let enabled = shape > 0.0;
  let emphasis = clamp(object.material.w, 0.0, 1.0);
  // Follow each physical face, including the sides of cubes and board bevels.
  let n = abs(input.localNormal);
  let facePosition = select(select(input.localPosition.xy, input.localPosition.yz, n.x > n.z), input.localPosition.xz, n.y > max(n.x, n.z));
  let rectangle = interactionEdgePixels(0.46 - max(abs(facePosition.x), abs(facePosition.y)));
  let circle = interactionEdgePixels(0.5 - length(input.localPosition.xz));
  let distance = abs(select(rectangle, circle, shape == 2.0));
  let normal = normalize(input.normal);
  let view = normalize(frame.eye.xyz - input.world);
  let grazing = 1.0 - abs(dot(normal, view));
  // Two smoothly fading widths create the luminous edge and its soft falloff.
  // Curved pieces use their real silhouette instead of drawing a ring at the foot.
  let core = select(exp(-distance * distance / 1.6), smoothstep(0.45, 0.80, grazing), shape == 3.0);
  let halo = select(exp(-distance * distance / 32.0), pow(grazing, 2.0), shape == 3.0);
  let lightColor = select(vec3<f32>(0.82, 0.94, 1.0), vec3<f32>(1.0, 0.63, 0.20), paying);
  let sheen = select(vec3<f32>(0.018, 0.020, 0.024), vec3<f32>(0.035, 0.018, 0.004), paying);
  let surface = min(color * (1.025 + emphasis * 0.025) + sheen * (1.0 + emphasis * 0.6), vec3<f32>(1.0));
  let glow = clamp(core * mix(0.72, 0.90, emphasis) + halo * mix(0.16, 0.24, emphasis), 0.0, 1.0);
  return select(color, mix(surface, lightColor, glow), enabled);
}
`
